import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  resendEmailRegistrationCode,
  startEmailRegistration,
  verifyEmailRegistration,
} from '../../../services/auth';
import {
  clearPendingRegistrationChallenge,
  persistPendingRegistrationChallenge,
  readPendingRegistrationChallenge,
} from '../../../services/authRegistrationChallengeStorage';
import { clearMessage } from '../../../reducers/message';
import LiquidGlassCard from '../LiquidGlassCard';
import PopupMessage from '../PopupMessage';
import AuthBrandTitle from '../AuthBrandTitle';
import { normalizeUsernameForDisplay } from '../../../utils/username';
import '../AuthSupportCard.css';
import './index.css';
import { EMAIL_PATTERN } from '../../../constants/authValidation';

const INITIAL_FORM_DATA = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const VERIFICATION_CODE_LENGTH = 6;

const parseServerTimestamp = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDuration = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const hasChallengeId = (value) => typeof value === 'string' && value.trim() !== '';
const resolveVerificationPassword = (value) => {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }
  return value;
};

const RegisterCard = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(() => {
    const pendingChallenge = readPendingRegistrationChallenge();
    return pendingChallenge
      ? {
        ...INITIAL_FORM_DATA,
        username: pendingChallenge.username || '',
        email: pendingChallenge.email,
      }
      : INITIAL_FORM_DATA;
  });
  const [challenge, setChallenge] = useState(() => readPendingRegistrationChallenge());
  const [verificationCode, setVerificationCode] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);
  const [message, setMessage] = useState('');
  const [popupContentClassName, setPopupContentClassName] = useState('popup-content--auth-default');
  const [popupTrigger, setPopupTrigger] = useState(0);
  const [now, setNow] = useState(Date.now());

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const verificationInputRef = useRef(null);

  useEffect(() => {
    dispatch(clearMessage());
    setMessage('');

    return () => {
      dispatch(clearMessage());
      setMessage('');
    };
  }, [dispatch]);

  useEffect(() => {
    if (!challenge) {
      return undefined;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [challenge]);

  useEffect(() => {
    if (!challenge) {
      return;
    }
    verificationInputRef.current?.focus();
  }, [challenge]);

  const resendCooldownRemaining = challenge
    ? Math.max(0, Math.ceil((challenge.resendAvailableAt - now) / 1000))
    : 0;
  const verificationWindowRemaining = challenge
    ? Math.max(0, Math.ceil((challenge.expiresAt - now) / 1000))
    : 0;
  const isChallengeExpired = Boolean(challenge) && verificationWindowRemaining === 0;
  const requiresFreshPassword = Boolean(challenge?.passwordRequired);

  const isStarting = loadingAction === 'start';
  const isVerifying = loadingAction === 'verify';
  const isResending = loadingAction === 'resend';

  useEffect(() => {
    if (challenge && verificationWindowRemaining === 0) {
      clearPendingRegistrationChallenge();
    }
  }, [challenge, verificationWindowRemaining]);

  const showPopup = (nextMessage) => {
    setMessage(nextMessage);
    setPopupContentClassName('popup-content--auth-default');
    setPopupTrigger((prev) => prev + 1);
  };

  const showInfoPopup = (nextMessage) => {
    setMessage(nextMessage);
    setPopupContentClassName('popup-content--auth-info');
    setPopupTrigger((prev) => prev + 1);
  };

  const updateChallengeState = (challengeResponse, email, username, options = {}) => {
    const issuedAt = parseServerTimestamp(challengeResponse?.issuedAt);
    const expiresAt = parseServerTimestamp(challengeResponse?.expiresAt);
    const resendAvailableAt = parseServerTimestamp(challengeResponse?.resendAvailableAt);
    const nextChallenge = persistPendingRegistrationChallenge({
      challengeId: challengeResponse?.challengeId,
      email,
      username,
      passwordRequired: challengeResponse?.passwordRequired === true,
      ...(Number.isFinite(issuedAt) ? { issuedAt } : {}),
      ...(Number.isFinite(expiresAt) ? { expiresAt } : {}),
      ...(Number.isFinite(resendAvailableAt) ? { resendAvailableAt } : {}),
      preserveExistingTiming: options.preserveExistingTiming === true,
    });
    setChallenge(nextChallenge);
    setVerificationCode('');
    setNow(Date.now());
    return nextChallenge;
  };

  const normalizeFormData = () => ({
    username: formData.username.trim(),
    email: formData.email.trim().toLowerCase(),
    password: formData.password,
    confirmPassword: formData.confirmPassword,
  });

  const getStartValidationMessage = () => {
    const normalized = normalizeFormData();
    if (!normalized.username || !normalized.email || !normalized.password || !normalized.confirmPassword) {
      return t('auth.common.validation.required');
    }
    if (normalized.username.length < 3 || normalized.username.length > 20) {
      return t('auth.register.validation.usernameLength');
    }
    if (!normalizeUsernameForDisplay(normalized.username)) {
      return t('auth.register.validation.usernameFormat');
    }
    if (normalized.email.length > 50 || !EMAIL_PATTERN.test(normalized.email)) {
      return t('auth.common.validation.invalidEmail');
    }
    if (normalized.password.length < 8 || normalized.password.length > 40) {
      return t('auth.register.validation.passwordLength');
    }
    if (normalized.password !== normalized.confirmPassword) {
      return t('auth.register.validation.passwordMismatch');
    }
    return null;
  };

  const getCodeValidationMessage = () => {
    if (!verificationCode) {
      return t('auth.common.validation.required');
    }
    if (!new RegExp(`^\\d{${VERIFICATION_CODE_LENGTH}}$`).test(verificationCode)) {
      return t('auth.register.validation.codeLength');
    }
    return null;
  };

  const getVerificationPasswordValidationMessage = () => {
    if (!requiresFreshPassword) {
      return null;
    }
    if (!formData.password || !formData.confirmPassword) {
      return t('auth.common.validation.required');
    }
    if (formData.password.length < 8 || formData.password.length > 40) {
      return t('auth.register.validation.passwordLength');
    }
    if (formData.password !== formData.confirmPassword) {
      return t('auth.register.validation.passwordMismatch');
    }
    return null;
  };

  const getRequestErrorMessage = (error, phase) => {
    const status = error?.response?.status;

    if (status === 429) {
      return t('auth.register.feedback.rateLimited');
    }

    if (phase === 'verify') {
      if (status === 400) {
        return t('auth.register.feedback.invalidCode');
      }
      if (status === 503) {
        return t('auth.register.feedback.verifyUnavailable');
      }
    }

    if (phase !== 'start' && (status === 404 || status === 409)) {
      return t('auth.register.feedback.challengeExpired');
    }

    return t('auth.register.feedback.genericError');
  };

  const resetVerificationStep = () => {
    clearPendingRegistrationChallenge();
    setChallenge(null);
    setVerificationCode('');
    setNow(Date.now());
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((currentFormData) => ({
      ...currentFormData,
      [name]: value,
    }));
  };

  const handleVerificationCodeChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, VERIFICATION_CODE_LENGTH);
    setVerificationCode(digitsOnly);
  };

  const handleStartRegistration = async (e) => {
    e.preventDefault();
    const validationMessage = getStartValidationMessage();
    if (validationMessage) {
      showPopup(validationMessage);
      return;
    }

    const normalized = normalizeFormData();
    setLoadingAction('start');

    try {
      const response = await dispatch(
        startEmailRegistration(normalized.username, normalized.email, normalized.password)
      );
      setFormData((currentFormData) => ({
        ...currentFormData,
        username: normalized.username,
        email: normalized.email,
      }));
      if (hasChallengeId(response?.challengeId)) {
        updateChallengeState(
          response,
          normalized.email,
          normalized.username,
          { preserveExistingTiming: true }
        );
        showPopup(t('auth.register.feedback.codeSent'));
      } else {
        resetVerificationStep();
        showInfoPopup(t('auth.register.feedback.startAcknowledged'));
      }
    } catch (error) {
      showPopup(getRequestErrorMessage(error, 'start'));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleVerifyRegistration = async (e) => {
    e.preventDefault();
    if (!challenge) {
      return;
    }
    if (isChallengeExpired) {
      showPopup(t('auth.register.feedback.challengeExpired'));
      return;
    }

    const validationMessage = getCodeValidationMessage();
    if (validationMessage) {
      showPopup(validationMessage);
      return;
    }
    const passwordValidationMessage = getVerificationPasswordValidationMessage();
    if (passwordValidationMessage) {
      showPopup(passwordValidationMessage);
      return;
    }

    setLoadingAction('verify');
    try {
      await dispatch(
        verifyEmailRegistration(
          challenge.challengeId,
          verificationCode,
          resolveVerificationPassword(formData.password)
        )
      );
      resetVerificationStep();
      navigate('/');
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 409) {
        clearPendingRegistrationChallenge();
        setChallenge((currentChallenge) => currentChallenge
          ? { ...currentChallenge, expiresAt: Date.now() }
          : currentChallenge);
      }
      showPopup(getRequestErrorMessage(error, 'verify'));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResendCode = async () => {
    if (!challenge || isChallengeExpired || resendCooldownRemaining > 0) {
      return;
    }

    setLoadingAction('resend');
    try {
      const response = await dispatch(resendEmailRegistrationCode(challenge.challengeId));
      updateChallengeState(
        response,
        challenge.email,
        challenge.username
      );
      showInfoPopup(t('auth.register.feedback.codeResent'));
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 409) {
        clearPendingRegistrationChallenge();
        setChallenge((currentChallenge) => currentChallenge
          ? { ...currentChallenge, expiresAt: Date.now() }
          : currentChallenge);
      }
      showPopup(getRequestErrorMessage(error, 'resend'));
    } finally {
      setLoadingAction(null);
    }
  };

  const resendLabel = resendCooldownRemaining > 0
    ? t('auth.register.actions.resendCountdown', { time: formatDuration(resendCooldownRemaining) })
    : t('auth.register.actions.resend');

  return (
    <>
      {message && (
        <PopupMessage
          message={message}
          popupTrigger={popupTrigger}
          onClose={() => setMessage('')}
          contentClassName={popupContentClassName}
        />
      )}

      <LiquidGlassCard
        title={<AuthBrandTitle label={t('auth.common.brand')} />}
        variant="auth"
        size="medium"
        className="register-card"
      >
        {!challenge && (
          <form onSubmit={handleStartRegistration} className="register-form" noValidate>
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                name="username"
                placeholder={t('auth.register.fields.username.placeholder')}
                aria-label={t('auth.register.fields.username.placeholder')}
                value={formData.username}
                onChange={onChange}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="username"
                pattern="[A-Za-z0-9_]{3,20}"
                minLength={3}
                maxLength={20}
              />
            </div>

            <div className="form-group">
              <input
                type="email"
                className="form-control"
                name="email"
                placeholder={t('auth.register.fields.email.placeholder')}
                aria-label={t('auth.register.fields.email.placeholder')}
                value={formData.email}
                onChange={onChange}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="email"
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                className="form-control"
                name="password"
                placeholder={t('auth.register.fields.password.placeholder')}
                aria-label={t('auth.register.fields.password.placeholder')}
                value={formData.password}
                onChange={onChange}
                autoComplete="new-password"
                minLength={6}
                maxLength={40}
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                className="form-control"
                name="confirmPassword"
                placeholder={t('auth.register.fields.confirmPassword.placeholder')}
                aria-label={t('auth.register.fields.confirmPassword.placeholder')}
                value={formData.confirmPassword}
                onChange={onChange}
                autoComplete="new-password"
                minLength={6}
                maxLength={40}
              />
            </div>

            <div className="form-group">
              <button className="btn login-btn" disabled={isStarting}>
                {isStarting && <span className="spinner-border spinner-border-sm"></span>}
                <span>{t('auth.register.actions.start')}</span>
              </button>
            </div>
          </form>
        )}

        {challenge && (
          <form onSubmit={handleVerifyRegistration} className="register-form register-form--verification" noValidate>
            <div className="register-step-panel">
              <p className="register-step-copy">
                {t('auth.register.descriptions.verify', { email: challenge.email })}
              </p>
              <p className="auth-form-note auth-form-note--reminder">
                {t('auth.common.emailDeliveryHint')}
              </p>

              <div className="register-step-status" aria-live="polite">
                {!isChallengeExpired && (
                  <span>{t('auth.register.status.expiresIn', { time: formatDuration(verificationWindowRemaining) })}</span>
                )}
                {isChallengeExpired && (
                  <span className="register-step-status register-step-status--expired">
                    {t('auth.register.feedback.challengeExpired')}
                  </span>
                )}
              </div>
            </div>

            {!isChallengeExpired && (
              <>
                <div className="form-group">
                  <input
                    ref={verificationInputRef}
                    type="text"
                    className="form-control register-code-input"
                    name="verificationCode"
                    placeholder={t('auth.register.fields.code.placeholder')}
                    aria-label={t('auth.register.fields.code.placeholder')}
                    value={verificationCode}
                    onChange={handleVerificationCodeChange}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\\d{6}"
                    maxLength={VERIFICATION_CODE_LENGTH}
                  />
                </div>

                {requiresFreshPassword && (
                  <>
                    <div className="form-group">
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        placeholder={t('auth.register.fields.password.placeholder')}
                        aria-label={t('auth.register.fields.password.placeholder')}
                        value={formData.password}
                        onChange={onChange}
                        autoComplete="new-password"
                        minLength={8}
                        maxLength={40}
                      />
                    </div>

                    <div className="form-group">
                      <input
                        type="password"
                        className="form-control"
                        name="confirmPassword"
                        placeholder={t('auth.register.fields.confirmPassword.placeholder')}
                        aria-label={t('auth.register.fields.confirmPassword.placeholder')}
                        value={formData.confirmPassword}
                        onChange={onChange}
                        autoComplete="new-password"
                        minLength={8}
                        maxLength={40}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <button className="btn login-btn" disabled={isVerifying}>
                    {isVerifying && <span className="spinner-border spinner-border-sm"></span>}
                    <span>{t('auth.register.actions.verify')}</span>
                  </button>
                </div>
              </>
            )}

            <div className="register-secondary-actions">
              {!isChallengeExpired && (
                <button
                  type="button"
                  className="register-secondary-button"
                  onClick={handleResendCode}
                  disabled={isResending || resendCooldownRemaining > 0}
                >
                  {isResending && <span className="spinner-border spinner-border-sm"></span>}
                  <span>{resendLabel}</span>
                </button>
              )}

              <button
                type="button"
                className="register-secondary-button"
                onClick={resetVerificationStep}
                disabled={isVerifying || isResending}
              >
                <span>{t(isChallengeExpired ? 'auth.register.actions.restart' : 'auth.register.actions.changeDetails')}</span>
              </button>
            </div>
          </form>
        )}

        <p className="auth-footer">
          {t('auth.register.footer.prompt')}{' '}
          <Link to="/login">{t('auth.register.footer.link')}</Link>
        </p>
      </LiquidGlassCard>
    </>
  );
};

export default RegisterCard;
